/**
 * ActivitiesPage — standalone activity log across the workspace.
 * Useful for "what did the team do this week?" view. No `regarding`
 * filter pre-applied; activity rows show their parent.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsActivities,
  useFiasLeadsContacts,
  useFiasLeadsCompanies,
  useFiasLeadsDeals,
  useDeleteFiasLeadsActivity,
} from '../hooks/useFiasLeads';
import type { FiasLeadsActivityKind } from '@/lib/api';
import { SkeletonRow } from '../components/Skeletons';

interface Props {
  holderId: string;
}

const KIND_COLORS: Record<FiasLeadsActivityKind, string> = {
  note: 'bg-slate-100 text-slate-700',
  call: 'bg-blue-100 text-blue-700',
  email: 'bg-emerald-100 text-emerald-700',
  meeting: 'bg-purple-100 text-purple-700',
};

// Sort by event time (occurred_at) by default; "Recently added" sorts by
// when the activity was logged (created_at), which differs from occurred_at
// for back-dated entries.
const ACTIVITY_SORTS = [
  { value: 'occurred_at:desc', label: 'Most recent', sortBy: 'occurred_at', sortDir: 'desc' },
  { value: 'occurred_at:asc', label: 'Oldest first', sortBy: 'occurred_at', sortDir: 'asc' },
  { value: 'created_at:desc', label: 'Recently added', sortBy: 'created_at', sortDir: 'desc' },
] as const;

export function ActivitiesPage({ holderId }: Props) {
  const [kindFilter, setKindFilter] = useState<FiasLeadsActivityKind | ''>('');
  const [sortValue, setSortValue] = useState<string>('occurred_at:desc');
  const sort = ACTIVITY_SORTS.find((s) => s.value === sortValue) ?? ACTIVITY_SORTS[0];
  const activitiesQuery = useFiasLeadsActivities(holderId, {
    kind: kindFilter || undefined,
    sortBy: sort.sortBy,
    sortDir: sort.sortDir,
    limit: 100,
  });
  const contactsQuery = useFiasLeadsContacts(holderId);
  const companiesQuery = useFiasLeadsCompanies(holderId);
  const dealsQuery = useFiasLeadsDeals(holderId);
  const deleteActivity = useDeleteFiasLeadsActivity(holderId);

  const handleDelete = (activityId: string, subject: string) => {
    if (!confirm(`Delete activity "${subject}"?`)) return;
    deleteActivity.mutate(activityId);
  };

  const contactNameById = new Map(
    (contactsQuery.data ?? []).map((c) => [
      c.contactId,
      `${c.firstName} ${c.lastName ?? ''}`.trim(),
    ]),
  );
  const companyNameById = new Map((companiesQuery.data ?? []).map((c) => [c.companyId, c.name]));
  const dealNameById = new Map((dealsQuery.data ?? []).map((d) => [d.id, d.name]));

  const activities = activitiesQuery.data ?? [];

  // Show the related record as "Type: Name" (resolving deals/contacts/
  // companies to their name, never a raw id) so each row makes clear what
  // it is about.
  function regardingLink(a: (typeof activities)[number]) {
    if (a.regardingType === 'vault_contact') {
      const name = contactNameById.get(a.regardingId) || 'Unknown contact';
      return (
        <span>
          <span className="font-medium text-foreground/80">Contact:</span>{' '}
          <Link to={`${ARCHE_PATH}/contacts/${a.regardingId}`} className="hover:underline">
            {name}
          </Link>
        </span>
      );
    }
    if (a.regardingType === 'vault_company') {
      const name = companyNameById.get(a.regardingId) || 'Unknown company';
      return (
        <span>
          <span className="font-medium text-foreground/80">Company:</span>{' '}
          <Link to={`${ARCHE_PATH}/companies/${a.regardingId}`} className="hover:underline">
            {name}
          </Link>
        </span>
      );
    }
    if (a.regardingType === 'fias_leads_deal') {
      const name = dealNameById.get(a.regardingId) || 'Unknown deal';
      return (
        <span>
          <span className="font-medium text-foreground/80">Deal:</span>{' '}
          <Link to={`${ARCHE_PATH}/deals/${a.regardingId}`} className="hover:underline">
            {name}
          </Link>
        </span>
      );
    }
    return null;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Activity log</h1>
          <p className="text-sm text-muted-foreground">
            All logged interactions across the workspace.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as FiasLeadsActivityKind | '')}
            className="rounded border border-border bg-card px-3 py-2 text-sm"
          >
            <option value="">All kinds</option>
            <option value="note">Notes</option>
            <option value="call">Calls</option>
            <option value="email">Emails</option>
            <option value="meeting">Meetings</option>
          </select>
          <select
            value={sortValue}
            onChange={(e) => setSortValue(e.target.value)}
            aria-label="Sort activity log"
            className="rounded border border-border bg-card px-3 py-2 text-sm"
          >
            {ACTIVITY_SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                Sort: {s.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      {activitiesQuery.isLoading && <SkeletonRow />}

      <div className="rounded border border-border bg-card">
        <ul className="divide-y divide-border">
          {activities.map((a) => (
            <li key={a.activityId} className="p-3 flex items-start gap-3">
              <span
                className={`shrink-0 rounded px-2 py-0.5 text-xs uppercase ${KIND_COLORS[a.kind]}`}
              >
                {a.kind}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm whitespace-pre-wrap">{a.bodyMarkdown || a.subject}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {regardingLink(a)} · {new Date(a.occurredAt).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => handleDelete(a.activityId, a.subject)}
                disabled={deleteActivity.isPending}
                aria-label="Delete activity"
                title="Delete activity"
                className="shrink-0 rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        {activities.length === 0 && !activitiesQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">No activities yet.</p>
        )}
      </div>
    </div>
  );
}
